import type { Ref } from '@nuxtjs/composition-api';
import { ref, readonly, useContext } from '@nuxtjs/composition-api';
import { Logger } from '~/helpers/logger';
import type {
  UseCrosssellProductsError,
  UseCrosssellProductsInterface,
  UseCrosssellProductsSearchParams,
} from './useCrosssellProducts';
import crosssellGql from './crosssell.gql';

/**
 * Allows loading crosssell products.
 *
 * See the {@link UseCrosssellProductsInterface} for a list of methods and values available in this composable.
 */
export function useCrossSellProducts(): UseCrosssellProductsInterface {
  const { app } = useContext();
  const loading = ref(false);
  const error: Ref<UseCrosssellProductsError> = ref({
    search: null,
  });

  const search = async (params: UseCrosssellProductsSearchParams) => {
    const {
      customQuery,
      ...searchParams
    } = params;

    let results = null;

    try {
      loading.value = true;

      Logger.debug('[Magento] Load crosssell products based on ', { searchParams });

      const { data } = await app.context.$vsf.$magento.api.customQuery({
        query: crosssellGql,
        queryVariables: searchParams
      });

      Logger.debug('[Result] crosssell products:', { data });
      results = data.products?.items[0]?.crosssell_products;
      error.value.search = null;
    } catch (err) {
      error.value.search = err;
      Logger.error('useCrosssellProducts/search', err);
    } finally {
      loading.value = false;
    }

    return results;
  };

  return {
    search,
    loading: readonly(loading),
    error: readonly(error),
  };
}

export default useCrossSellProducts;
export * from './useCrosssellProducts';
